if (!customElements.get('store-open-status')) {
  class StoreOpenStatus extends HTMLElement {
    connectedCallback() {
      this.labelEl = this.querySelector('[data-store-open-status-label]');
      this.update();
    }

    update() {
      const timezone = this.dataset.timezone;
      const hoursRaw = this.dataset.hours;
      if (!timezone || !hoursRaw || !this.labelEl) return;

      let hours;
      try {
        hours = JSON.parse(hoursRaw);
      } catch (error) {
        return;
      }

      const periods = hours?.periods;
      if (!Array.isArray(periods) || periods.length === 0) return;

      const dayLabels = this.getDayLabels();
      const now = this.getZonedNow(timezone);
      if (!now) return;

      const intervals = this.buildIntervals(periods);
      if (intervals.length === 0) return;

      const current = this.findCurrentInterval(intervals, now.weekMinutes);
      let label = '';
      let isOpen = false;

      if (current) {
        isOpen = true;
        const closeTime = this.formatTime(current.closeHour, current.closeMinute);
        label = this.interpolate(this.dataset.i18nOpenUntil, { time: closeTime });
      } else {
        const next = this.findNextOpen(intervals, now.weekMinutes);
        if (!next) return;

        const openTime = this.formatTime(next.openHour, next.openMinute);
        const opensToday = next.openDay === now.day;

        label = opensToday
          ? this.interpolate(this.dataset.i18nClosedOpensAt, { time: openTime })
          : this.interpolate(this.dataset.i18nClosedOpensDay, {
              day: dayLabels[next.openDay] || '',
              time: openTime,
            });
      }

      if (!label) return;

      this.labelEl.textContent = label;
      this.classList.toggle('is-open', isOpen);
      this.classList.toggle('is-closed', !isOpen);
      this.hidden = false;
    }

    getDayLabels() {
      const raw = this.dataset.dayLabels || '';
      if (raw.includes(',')) {
        return raw.split(',').map((label) => label.trim());
      }

      return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    }

    getZonedNow(timeZone) {
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone,
          weekday: 'short',
          hour: 'numeric',
          minute: 'numeric',
          hourCycle: 'h23',
        });
        
        const parts = {};
        formatter.formatToParts(new Date()).forEach((part) => {
          if (part.type !== 'literal') parts[part.type] = part.value;
        });

        const weekdayMap = {
          Sun: 0,
          Mon: 1,
          Tue: 2,
          Wed: 3,
          Thu: 4,
          Fri: 5,
          Sat: 6,
        };
        const day = weekdayMap[parts.weekday];
        if (day === undefined) return null;

        const hour = Number(parts.hour) % 24;
        const minute = Number(parts.minute);

        return {
          day,
          hour,
          minute,
          weekMinutes: day * 1440 + hour * 60 + minute,
        };
      } catch (error) {
        return null;
      }
    }

    buildIntervals(periods) {
      const weekMinutes = 7 * 1440;
      const intervals = [];

      periods.forEach((period) => {
        const open = period?.open;
        if (!open || open.day === undefined || open.hour === undefined) return;

        const openDay = Number(open.day);
        const openHour = Number(open.hour);
        const openMinute = Number(open.minute || 0);
        let openAt = openDay * 1440 + openHour * 60 + openMinute;

        if (!period.close) {
          intervals.push({
            openAt,
            closeAt: openAt + 1440,
            openDay,
            openHour,
            openMinute,
            closeHour: openHour,
            closeMinute: openMinute,
          });
          return;
        }

        const close = period.close;
        const closeDay = Number(close.day);
        const closeHour = Number(close.hour);
        const closeMinute = Number(close.minute || 0);
        let closeAt = closeDay * 1440 + closeHour * 60 + closeMinute;

        if (closeAt <= openAt) closeAt += weekMinutes;

        intervals.push({
          openAt,
          closeAt,
          openDay,
          openHour,
          openMinute,
          closeHour,
          closeMinute,
        });
      });

      return intervals.sort((a, b) => a.openAt - b.openAt);
    }

    findCurrentInterval(intervals, weekMinutes) {
      const weekSize = 7 * 1440;
      return (
        intervals.find((interval) => {
          return (
            (weekMinutes >= interval.openAt && weekMinutes < interval.closeAt) ||
            (weekMinutes + weekSize >= interval.openAt && weekMinutes + weekSize < interval.closeAt)
          );
        }) || null
      );
    }

    findNextOpen(intervals, weekMinutes) {
      const weekSize = 7 * 1440;
      let next = null;
      let bestDelta = Infinity;

      intervals.forEach((interval) => {
        let delta = interval.openAt - weekMinutes;
        if (delta <= 0) delta += weekSize;
        if (delta < bestDelta) {
          bestDelta = delta;
          next = interval;
        }
      });

      return next;
    }

    formatTime(hour, minute) {
      return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    }

    interpolate(template, values) {
      if (!template) return '';
      return template.replace(/\{(\w+)\}/g, (_, key) => values[key] ?? '');
    }
  }

  customElements.define('store-open-status', StoreOpenStatus);
}
